import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './IncomingCallModal.module.css';
import { hasTwilioAccess } from '../../utils/userRole';

/**
 * Modal rediseñado para mostrar y gestionar llamadas entrantes de Twilio
 * Props:
 * - open: boolean (si el modal está visible)
 * - from: string (número llamante)
 * - contactInfo: object (información del contacto si es conocido)
 * - onAccept: función para aceptar la llamada
 * - onReject: función para rechazar la llamada
 */
export default function IncomingCallModal({ 
  open, 
  from, 
  contactInfo, 
  onAccept, 
  onReject 
}) {
  const navigate = useNavigate();
  const [isRinging, setIsRinging] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  
  // 🚫 Verificar acceso a Twilio (después de los hooks)
  const userHasTwilioAccess = hasTwilioAccess();

  // Efecto de animación de llamada
  useEffect(() => {
    if (open) {
      setIsRinging(true);
      const timer = setInterval(() => {
        setCallDuration(prev => prev + 1);
      }, 1000);
      
      return () => {
        clearInterval(timer);
        setCallDuration(0);
        setIsRinging(false);
      };
    }
  }, [open]);
  
  // Si no tiene acceso, no renderizar nada (DESPUÉS de todos los hooks)
  if (!userHasTwilioAccess) {
    return null;
  }

  // Formatear duración de llamada
  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Manejar click en expediente
  const handleExpedienteClick = (expedienteId) => {
    if (expedienteId) {
      // Navegar al expediente sin cerrar la llamada
      navigate(`/record/${expedienteId}`);
    }
  };

  // Obtener información del contacto
  const getContactDisplay = () => {
    if (contactInfo) {
      return {
        name: contactInfo.name || contactInfo.nombre || 'Contacto conocido',
        type: contactInfo.type || contactInfo.tipo || 'Contacto',
        phone: from || contactInfo.phone || contactInfo.telefono,
        company: contactInfo.company || contactInfo.compania,
        isKnown: true
      };
    }
    
    return {
      name: 'Número desconocido',
      type: 'Llamada entrante',
      phone: from || 'Sin número',
      company: null,
      isKnown: false
    };
  };

  const contact = getContactDisplay();

  if (!open) return null;

  return (
    <div className={styles.overlay}>
      <div className={`${styles.modal} ${isRinging ? styles.ringing : ''}`}>
        {/* Header con estado de llamada */}
        <div className={styles.header}>
          <div className={styles.statusIndicator}>
            <div className={`${styles.pulseRing} ${isRinging ? styles.active : ''}`}>
              <div className={styles.pulseRingInner}></div>
            </div>
            <span className={styles.statusText}>Llamada entrante</span>
          </div>
          <div className={styles.headerActions}>
            <div className={styles.duration}>{formatDuration(callDuration)}</div>
          </div>
        </div>

        {/* Avatar y información del contacto */}
        <div className={styles.contactSection}>
          <div className={styles.avatar}>
            {contact.isKnown ? (
              <div className={styles.knownContactAvatar}>
                {contact.name.charAt(0).toUpperCase()}
              </div>
            ) : (
              <div className={styles.unknownContactAvatar}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                </svg>
              </div>
            )}
          </div>

          <div className={styles.contactInfo}>
            <h2 className={styles.contactName}>{contact.name}</h2>
            <p className={styles.contactType}>
              {contact.isKnown && (
                <span className={styles.knownBadge}>
                  ✓ {contact.type}
                </span>
              )}
              {!contact.isKnown && (
                <span className={styles.unknownBadge}>
                  ? {contact.type}
                </span>
              )}
            </p>
            <p className={styles.phoneNumber}>{contact.phone}</p>
            {contact.company && (
              <p className={styles.company}>📋 {contact.company}</p>
            )}
          </div>
        </div>

        {/* Información adicional si es contacto conocido */}
        {contact.isKnown && contactInfo && (
          <div className={styles.additionalInfo}>
            {contactInfo.expediente && (
              <div 
                className={`${styles.infoChip} ${styles.clickableChip}`}
                onClick={() => handleExpedienteClick(contactInfo.expedienteId)}
                title="Click para abrir el expediente"
              >
                📁 Expediente: {contactInfo.expediente}
              </div>
            )}
            
            {/* ✨ Información del encargado */}
            {contactInfo.encargado && (
              <div className={styles.infoChip}>
                👨‍💼 Encargado: {contactInfo.encargado}
              </div>
            )}
            
            {/* ✨ Información de llamadas perdidas entrantes (ellos nos llamaron) */}
            {contactInfo.tieneLlamadasPerdidas && (
              <div className={`${styles.infoChip} ${styles.missedCallChip}`}>
                📞⬅️❌ {contactInfo.numeroLlamadasPerdidas} llamada{contactInfo.numeroLlamadasPerdidas > 1 ? 's' : ''} perdida{contactInfo.numeroLlamadasPerdidas > 1 ? 's' : ''} de {contact.name} (24h)
                {contactInfo.tiempoUltimaLlamadaPerdida && (
                  <span className={styles.missedCallTime}>
                    • Última: {contactInfo.tiempoUltimaLlamadaPerdida}
                  </span>
                )}
              </div>
            )}
            
            {/* ✨✨ Información de llamadas salientes no contestadas (nosotros les llamamos) */}
            {contactInfo.tieneLlamadasSalientesPerdidas && (
              <div className={`${styles.infoChip} ${styles.outgoingMissedCallChip}`}>
                📞➡️❌ Llamada no contestada {contactInfo.tiempoUltimaLlamadaSalientePerdida} por {contact.name}
                {contactInfo.numeroLlamadasSalientesPerdidas > 1 && (
                  <span className={styles.missedCallTime}>
                    • {contactInfo.numeroLlamadasSalientesPerdidas} intentos en 24h
                  </span>
                )}
              </div>
            )}
          </div>
        )}

        {/* Acciones */}
        <div className={styles.actions}>
          <button 
            className={`${styles.actionButton} ${styles.reject}`} 
            onClick={onReject}
            title="Rechazar llamada"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.38,10.79l-2.2-2.2c-.28-.28-.36-.67-.21-1.02.12-.28.4-.47.72-.47h1.18C17.47,5.02,14.92,3.5,12,3.5c-2.92,0-5.47,1.52-6.87,3.8h1.18c.32,0,.6.19.72.47.15.35.07.74-.21,1.02l-2.2,2.2c-.28.28-.72.28-1,0l-2.2-2.2c-.28-.28-.36-.67-.21-1.02.12-.28.4-.47.72-.47h1.95C4.84,4.16,8.18,1.5,12,1.5s7.16,2.66,8.32,5.8h1.95c.32,0,.6.19.72.47.15.35.07.74-.21,1.02l-2.2,2.2c-.28.28-.72.28-1,0z"/>
            </svg>
            <span>Rechazar</span>
          </button>

          <button 
            className={`${styles.actionButton} ${styles.accept}`} 
            onClick={onAccept}
            title="Aceptar llamada"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M20.01 15.38c-1.23 0-2.42-.2-3.53-.56-.35-.12-.74-.03-1.01.24l-1.57 1.97c-2.83-1.35-5.48-3.9-6.89-6.83l1.95-1.66c.27-.28.35-.67.24-1.02-.37-1.11-.56-2.3-.56-3.53 0-.54-.45-.99-.99-.99H4.19C3.65 3 3 3.24 3 3.99 3 13.28 10.73 21 20.01 21c.71 0 .99-.63.99-1.18v-3.45c0-.54-.45-.99-.99-.99z"/>
            </svg>
            <span>Aceptar</span>
          </button>
        </div>


      </div>
    </div>
  );
}
